import React, {useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import AniListLoginButton from './AniListLoginButton.js';
import '../../main-css/login-css/ProfilePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_PROD_URL;

const ProfilePage = () =>{
    const [user, setUser] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('accountInfo');


    useEffect(() => {
        const fetchProfile = async () => {
            try{
                let response = await fetch(`${BACKEND_URL}/valid/profile`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
    
                if (response.status === 401) {
                    // Try refreshing the token
                    await fetch(`${BACKEND_URL}/valid/refresh-token`, {
                        method: 'POST',
                        credentials: 'include',
                    });
    
                    // Retry fetching the profile
                    response = await fetch(`${BACKEND_URL}/valid/profile`, {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });
                }
    
                if (response.ok) {
                    const data = await response.json();
                    setUser(data.user);
                } else {
                    navigate('/valid/login');
                }
            } catch (err) {
                setErrorMessage('Failed to load profile. Please try again.');
            }
        };

        fetchProfile();
    }, [navigate]);

    //#region Change Functions

    // Handle account verification
    const handleVerifyAccount = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/valid/profile/verify-email`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                alert('A verification email has been sent. Please check your inbox.');
            } else {
                const errorData = await response.json();
                setErrorMessage(errorData.error);
            }
        } catch (err) {
            setErrorMessage('Failed to send verification email. Please try again.');
        }
    };

    //Logout
    const handleLogout = async () =>{
        try{
            await fetch(`${BACKEND_URL}/valid/logout`,{
                method: 'POST',
                credentials: 'include',
            });
            navigate('/valid/login');
        } catch (err) {
            setErrorMessage('Logout failed. Please try again.')
        }
    };

    //Logout Anilist
    const handleLogoutAnilist = async () =>{
        try{
            await fetch(`${BACKEND_URL}/valid/logout/anilist`,{
                method: 'POST',
                credentials: 'include',
            });
            navigate('/valid/login');
        } catch (err) {
            setErrorMessage('Anilist Logout failed. Please try again.')
        }
    };

    // Change Email Function
    const handleChangeEmail = async () => {
        const emailInput = document.querySelector('input[type="email"]').value;
        if (!emailInput) {
            setErrorMessage('Email cannot be empty.');
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/valid/profile/email`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ newEmail: emailInput }),
            });

            if (response.ok) {
                await updateProfile();
                alert('Email updated successfully');
                setErrorMessage(''); // Clear error after success
            } else {
                const errorData = await response.json();
                setErrorMessage(errorData.error);
            }
        } catch (err) {
            setErrorMessage('Failed to update email. Please try again.');
        }
    };

    // Change Username Function
    const handleChangeUsername = async () => {
        const usernameInput = document.querySelector('input[type="text"]').value;
        if (!usernameInput) {
            setErrorMessage('Username cannot be empty.');
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/valid/profile/username`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ newUsername: usernameInput }),
            });

            if (response.ok) {
                await updateProfile();
                alert('Username updated successfully');
                setErrorMessage(''); // Clear error after success
            } else {
                const errorData = await response.json();
                setErrorMessage(errorData.error);
            }
        } catch (err) {
            setErrorMessage('Failed to update username. Please try again.');
        }
    };

    // Change Password Function
    const handleChangePassword = async () => {
        const currentPassword = document.querySelector('input[type="password"]:first-child').value;
        const newPassword = document.querySelector('input[type="password"]:last-child').value;

        if (!currentPassword || !newPassword) {
            setErrorMessage('Both fields are required.');
            return;
        }

        if (currentPassword === newPassword) {
            setErrorMessage('New password cannot be the same as the current password.');
            return;
        }

        try {
            const response = await fetch(`${BACKEND_URL}/valid/profile/password`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ currentPassword, newPassword }),
            });

            if (response.ok) {
                alert('Password updated successfully');
                setErrorMessage(''); // Clear error after success
            } else {
                const errorData = await response.json();
                setErrorMessage(errorData.error);
            }
        } catch (err) {
            setErrorMessage('Failed to update password. Please try again.');
        }
    };

    //Update Profile Frontend
    const updateProfile = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/valid/profile`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
    
            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
            } else {
                console.error('Failed to fetch updated profile.');
            }
        } catch (error) {
            console.error('Error fetching updated profile:', error);
        }
    };
    //#endregion

    //#region Display Sections
    const renderContent = () => {
        if (!user) return <p>Loading...</p>;
    
        // Content based on active section
        switch (activeSection) {
            case 'accountInfo':
                return (
                    <div className="main-box">
                        <h2>Account Info</h2>
                        <p><strong>Username:</strong> {user.username}</p>
                        <p><strong>Email:</strong> {user.email}</p>
                        <button><AniListLoginButton /></button>
                        <button onClick={handleLogout}>Sign Out</button>
                        <button onClick={handleLogoutAnilist}>Remove Anilist Connection</button>
                        {!user.email_verified && (
                            <button onClick={handleVerifyAccount}>Verify Account</button>
                        )}
                        {errorMessage && <p className="error-text">{errorMessage}</p>} {/* Show error below buttons */}
                    </div>
                );
            case 'changeUsername':
                return (
                    <div className="main-box">
                        <h2>Change Username</h2>
                        <input type="text" placeholder="New Username" />
                        <button onClick={handleChangeUsername}>Update Username</button>
                        {errorMessage && <p className="error-text">{errorMessage}</p>} {/* Show error below button */}
                    </div>
                );
            case 'changePassword':
                return (
                    <div className="main-box">
                        <h2>Change Password</h2>
                        <input type="password" placeholder="New Password" />
                        <input type="password" placeholder="Confirm Password" />
                        <button onClick={handleChangePassword}>Update Password</button>
                        {errorMessage && <p className="error-text">{errorMessage}</p>} {/* Show error below button */}
                    </div>
                );
            case 'changeEmail':
                return (
                    <div className="main-box">
                        <h2>Change Email</h2>
                        <input type="email" placeholder="New Email" />
                        <button onClick={handleChangeEmail}>Update Email</button>
                        {errorMessage && <p className="error-text">{errorMessage}</p>} {/* Show error below button */}
                    </div>
                );
            default:
                return null;
        }
    };
    //#endregion
    return(
        <div className="settings-page">
            <div className="sidebar">
                <div
                    className={`tile ${activeSection === 'accountInfo' ? 'active' : ''}`}
                    onClick={() => setActiveSection('accountInfo')}
                >
                    Account Info
                </div>
                <div
                    className={`tile ${activeSection === 'changeUsername' ? 'active' : ''}`}
                    onClick={() => setActiveSection('changeUsername')}
                >
                    Change Username
                </div>
                <div
                    className={`tile ${activeSection === 'changePassword' ? 'active' : ''}`}
                    onClick={() => setActiveSection('changePassword')}
                >
                    Change Password
                </div>
                <div
                    className={`tile ${activeSection === 'changeEmail' ? 'active' : ''}`}
                    onClick={() => setActiveSection('changeEmail')}
                >
                    Change Email
                </div>
            </div>
            <div className="main-container">
                {renderContent()}
            </div>
        </div>
    );

};

export default ProfilePage;